// Import metadata files directly
import jazzMetadata from './data/composer_jazz_metadata.json';
import rockMetadata from './data/composer_rock_metadata.json';
import countryMetadata from './data/composer_country_metadata.json';
import edmMetadata from './data/composer_edm_metadata.json';
import orchestralMetadata from './data/composer_orchestral_metadata.json';

// Import 1st pass metadata
import jazzPass1Metadata from './data/composer_jazz_pass1_metadata.json';
import rockPass1Metadata from './data/composer_rock_pass1_metadata.json';
import countryPass1Metadata from './data/composer_country_pass1_metadata.json';
import edmPass1Metadata from './data/composer_edm_pass1_metadata.json';
import orchestralPass1Metadata from './data/composer_orchestral_pass1_metadata.json';

// Define composer-like generation examples with all variations
export const composerExamples = [
    {
        id: 'rock',
        displayName: 'Set 01',
        style: 'Rock',
        baseFolder: 'composer-like/2025-08-29_13-30-43_from_scratch_439431',
        pass1Metadata: rockPass1Metadata,
        variations: [
            {
                id: 'a',
                displayName: 'Var 01-1',
                basePath: 'composer-like/2025-08-29_13-30-43_from_scratch_439431/cond_gens/2025-08-29_13-40-50_cond_gen_9a907d',
                metadata: rockMetadata
            }
        ]
    },
    {
        id: 'jazz',
        displayName: 'Set 02',
        style: 'Jazz',
        baseFolder: 'composer-like/2025-08-28_13-33-25_from_scratch_a4bb19',
        pass1Metadata: jazzPass1Metadata,
        variations: [
            {
                id: 'a',
                displayName: 'Var 02-1',
                basePath: 'composer-like/2025-08-28_13-33-25_from_scratch_a4bb19/cond_gens/2025-08-28_13-34-30_cond_gen_5350c9',
                metadata: jazzMetadata
            },
            {
                id: 'b',
                displayName: 'Var 02-2',
                basePath: 'composer-like/2025-08-28_13-33-25_from_scratch_a4bb19/cond_gens/2025-08-28_13-39-59_cond_gen_02b2fb',
                metadata: null // Will be loaded dynamically
            }
        ]
    },
    {
        id: 'country',
        displayName: 'Set 03',
        style: 'Country',
        baseFolder: 'composer-like/2025-08-28_11-09-47_from_scratch_55cf95',
        pass1Metadata: countryPass1Metadata,
        variations: [
            {
                id: 'a',
                displayName: 'Var 03-1',
                basePath: 'composer-like/2025-08-28_11-09-47_from_scratch_55cf95/cond_gens/2025-08-28_11-20-53_cond_gen_4d050c',
                metadata: countryMetadata
            },
            {
                id: 'b',
                displayName: 'Var 03-2',
                basePath: 'composer-like/2025-08-28_11-09-47_from_scratch_55cf95/cond_gens/2025-08-28_11-14-05_cond_gen_e33422',
                metadata: null // Will be loaded dynamically
            },
            {
                id: 'c',
                displayName: 'Var 03-3',
                basePath: 'composer-like/2025-08-28_11-09-47_from_scratch_55cf95/cond_gens/2025-08-28_11-18-12_cond_gen_f5283a',
                metadata: null // Will be loaded dynamically
            }
        ]
    },
    {
        id: 'edm',
        displayName: 'Set 04',
        style: 'EDM',
        baseFolder: 'composer-like/2025-08-28_11-50-43_from_scratch_fb8635',
        pass1Metadata: edmPass1Metadata,
        variations: [
            {
                id: 'a',
                displayName: 'Var 04-1',
                basePath: 'composer-like/2025-08-28_11-50-43_from_scratch_fb8635/cond_gens/2025-08-28_12-12-01_cond_gen_f061fe',
                metadata: edmMetadata
            },
            {
                id: 'b',
                displayName: 'Var 04-2',
                basePath: 'composer-like/2025-08-28_11-50-43_from_scratch_fb8635/cond_gens/2025-08-28_12-06-48_cond_gen_09201b',
                metadata: null // Will be loaded dynamically
            }
        ]
    },
    {
        id: 'orchestral',
        displayName: 'Set 05',
        style: 'Orchestral',
        baseFolder: 'composer-like/2025-08-28_10-20-04_from_scratch_5298f4',
        pass1Metadata: orchestralPass1Metadata,
        variations: [
            {
                id: 'a',
                displayName: 'Var 05-1',
                basePath: 'composer-like/2025-08-28_10-20-04_from_scratch_5298f4/cond_gens/2025-08-28_10-42-39_cond_gen_c99868',
                metadata: orchestralMetadata
            },
            {
                id: 'b',
                displayName: 'Var 05-2',
                basePath: 'composer-like/2025-08-28_10-20-04_from_scratch_5298f4/cond_gens/2025-08-28_10-25-40_cond_gen_cede1e',
                metadata: null // Will be loaded dynamically
            },
            {
                id: 'c',
                displayName: 'Var 05-3',
                basePath: 'composer-like/2025-08-28_10-20-04_from_scratch_5298f4/cond_gens/2025-08-28_10-32-20_cond_gen_b7903f',
                metadata: null // Will be loaded dynamically
            }
        ]
    }
];

// Helper function to get audio path for composer examples (now uses variation)
export const getComposerAudioPath = (variation, stemType) => {
    const basePath = `${process.env.PUBLIC_URL}/audio/${variation.basePath}`;

    if (stemType === 'mix') {
        return `${basePath}/output_mix.mp3`;
    } else {
        return `${basePath}/${stemType}/output.mp3`;
    }
};

// Helper function to process composer example metadata (now uses variation)
export const loadComposerExampleMetadata = (variation) => {
    try {
        const metadata = variation.metadata;

        if (!metadata || !metadata.output_stems) {
            console.error('Invalid metadata for variation:', variation.id);
            return null;
        }

        return {
            prompt: metadata.prompt || 'No description available',
            stems: metadata.output_stems.map(stemType => ({
                type: stemType,
                folder: stemType
            }))
        };
    } catch (error) {
        console.error('Error processing composer metadata for variation:', variation.id, error);
        return null;
    }
};

// Helper function to get current variation for an example
export const getCurrentVariation = (example, variationId = 'a') => {
    return example.variations.find(v => v.id === variationId) || example.variations[0];
};

// Helper function to dynamically load metadata for a variation
export const loadVariationMetadata = async (variation) => {
    if (variation.metadata) {
        // Already loaded
        return variation.metadata;
    }
    
    try {
        // Load metadata from gen_metadata.json
        const metadataPath = `${process.env.PUBLIC_URL}/audio/${variation.basePath}/gen_metadata.json`;
        const response = await fetch(metadataPath);
        
        if (!response.ok) {
            console.warn(`Could not load metadata from ${metadataPath}`);
            return null;
        }
        
        const metadata = await response.json();
        // Cache the loaded metadata
        variation.metadata = metadata;
        return metadata;
    } catch (error) {
        console.error('Error loading variation metadata:', error);
        return null;
    }
};
